import React, { useContext, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  CircularProgress,
  TextField,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { api } from "../../../redux/api";
import { treeActions } from "../../../redux/slices/treeSlice";
import { ParentScreenCtx } from "/src/pages/workbench_build/WorkbenchBuild";
import { newTemplateActions } from "../../../redux/slices/newTemplateSlice";
import VersionConflictDialog from "../../../pages/deploy/ContextMenuActions/VersionConflictDialog";
const SCREEN_NAME ={
  inboundTemplate: "inbound_templates/",
  INBOUND: "inbound_templates/",
  outboundTemplate: "outbound_templates/",
  OUTBOUND: "outbound_templates/",
  workflow: "",
  WORKFLOW: ""
}

const getTemplatePath = (account, workflowGroup, type, title) => {
  return `${account}/${workflowGroup}/${SCREEN_NAME[type]}${title}`;
};

function DeployToServer({ open, modalOpen, toggle }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const parentScreen = useContext(ParentScreenCtx);
  const { account } = useSelector(state => state.queryParams);
  const { leftTree } = useSelector((state) => state.treeData[parentScreen]);
  const templateHeader = useSelector((state) => state.treeData[parentScreen]?.rightTree?.tree?.templateHeader);
  const selectedNode = leftTree.tree.body.selectedNode;
  
  const [isDeploying, setIsDeploying] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('');
  const [comment, setComment] = useState('');
  const [showVersionWarning, setShowVersionWarning] = useState(false);
  const [conflictItem, setConflictItem] = useState(null);

  // Get the appropriate endpoint based on screen type
  const getDeployEndpoint = () => {
    switch (parentScreen) {
      case 'inbound':
        return 'deployInboundTemplateToServer';
      case 'outbound':
        return 'deployOutboundTemplateToServer';
      case 'workflow':
        return 'deployWorkflowTemplateToServer';
      default:
        return null;
    }
  };

  const getTreeListEndpoint = () => {
    switch (parentScreen) {
      case 'inbound':
        return 'getInboundTemplateList';
      case 'outbound':
        return 'getOutboundTemplateList';
      case 'workflow':
        return 'getWorkflows';
      default:
        return null;
    }
  };

  const getTemplateFetchEndpoint = () => {
    switch (parentScreen) {
      case 'inbound':
        return 'getInboundTemplate';
      case 'outbound':
        return 'getOutboundTemplate';
      case 'workflow':
        return 'getWorkflowTree';
      default:
        return null;
    }
  };

  const refetchTemplate = async () => {
    const clearTemplateData = () => {
      ["header", "body", "trailer"].forEach(e => {
        dispatch(treeActions.setRightTreeData({screen: parentScreen, type: e, data: []}));
        dispatch(treeActions.setNewRightTreeTranslatedData({screen: parentScreen, type: e, data: [], templateChanged: false}))
        dispatch(treeActions.setRightTreeSelectedNode({screen: parentScreen, type: e, data: {}}))
      })
      dispatch(treeActions.setRightTreeInitialTemplateHeader({screen: parentScreen, value: []}));
      dispatch(treeActions.setRightTreeTemplateHeader({screen: parentScreen, value: [], templateChanged: false}));
    }
   
    const templateFetchEndpoint = getTemplateFetchEndpoint();
    
    if(!templateFetchEndpoint) {
      return;
    }
    try {
      // clear existing template data
      clearTemplateData();
      
      dispatch(treeActions.setRightTreeLoading({screen: parentScreen, data: true}));

      dispatch(treeActions.setRightTreePath({
        screen: parentScreen,
        workflowGroup: selectedNode?.workflowGroup,
        template: selectedNode?.title
      }));
      
     const response = await dispatch(api.endpoints[templateFetchEndpoint].initiate({ 
        account,
        workflowGroupName: encodeURIComponent(selectedNode.workflowGroup),
        templateName: selectedNode.title
      }, { forceRefetch: true }))
    
    return response;
    } 
    catch (error) {
      console.error('Error refetching template:', error);
      dispatch(treeActions.setTemplateFetchError({screen: parentScreen, data: true}));
      dispatch(treeActions.setRightTreeLoading({screen: parentScreen, data: false}));
    }
    finally {
      dispatch(treeActions.setRightTreeLoading({screen: parentScreen, data: false}));
    }
  }

  // Check for version conflicts using revision history
  // Uses template header REVISION_ID as userspace revision and API revisionId as server revision
  const checkForVersionConflicts = async () => {
    try {
      const path = getTemplatePath(account, selectedNode.workflowGroup, selectedNode.type, selectedNode.title);
      
      const response = await dispatch(api.endpoints.getServerRevisionhistory.initiate({
        account,
        path,
      }, {forceRefetch: true}));

      if (response.data && response.data.data.length > 0) {
        const latestRevision = response.data.data[0];
        //Get userspace revision from template header
        const userspaceRevision = parseInt(templateHeader?.REVISION_ID) || 0;
        
        // Server revision from API
        const serverRevision = parseInt(latestRevision.revisionId === "null" ? "0" : latestRevision.revisionId ) || 0;
        // Check if server has newer version than userspace template
        if (userspaceRevision < serverRevision) {
          const conflictItem = {
            title: selectedNode.title,
            workflowGroup: selectedNode.workflowGroup,
            type: selectedNode.type,
            userspace: { localRevisionId: userspaceRevision },
            server: { revisionId: serverRevision },
            error: `Server has newer version (revision ${serverRevision}) than template (revision ${userspaceRevision})`
          };
          return conflictItem;
        }
      }
      
      return null; 
    } catch (error) {
      console.error('Error checking version conflicts:', error);
      return null;
    }
  };

  const handleVersionConflictResponse = (decision) => {
    if (decision === 'YES' || decision === 'YES_TO_ALL') {
      // Proceed with deployment
      setShowVersionWarning(false);
      proceedWithDeploy();
    } else if (decision === 'NO' || decision === 'NO_TO_ALL') {
      // Cancel deployment
      setShowVersionWarning(false);
      dispatch(newTemplateActions.setShowAlert(true));
      dispatch(newTemplateActions.setAlertMessage('Deployment cancelled.'));
      dispatch(newTemplateActions.setAlertType('info'));
      toggle();
    }
  };

  const handleDeploy = async () => {
    if (!selectedNode || !selectedNode.isLeaf) {
      setAlertMessage(t('BOX_SELECT_TEMPLATE_TO_DEPLOY'));
      setAlertType('error');
      return;
    }

    if (!comment.trim()) {
      setAlertMessage(t('LBL_NEW_COMMENT_REQUIRED'));
      setAlertType('error');
      return;
    }

    // Check for version conflicts before proceeding
    const detectedConflict = await checkForVersionConflicts();
    
    if (detectedConflict) {
      setConflictItem(detectedConflict);
      setShowVersionWarning(true);
      return;
    }

    // No conflicts, proceed with deployment
    proceedWithDeploy();
  };

  const proceedWithDeploy = async () => {
    setIsDeploying(true);
    setAlertMessage('');

    const deployEndpoint = getDeployEndpoint();
    const treeListEndpoint = getTreeListEndpoint();

    if (!deployEndpoint || !treeListEndpoint) {
      setAlertMessage(t('BOX_MSG_GENERAL_ERROR'));
      setAlertType('error');
      setIsDeploying(false);
      return;
    }

    try {
      const deployParams = {
        account,
        workflowGroupName: selectedNode.workflowGroup,
        templateName: selectedNode.title,
        comment: comment.trim()
      };

      const response = await dispatch(api.endpoints[deployEndpoint].initiate(deployParams)).unwrap();

      if (response.current_state === "SUCCESS") {
    
        dispatch(newTemplateActions.setShowAlert(true));
        dispatch(newTemplateActions.setAlertMessage(response.message) || t('MSG_DEPLOY_TEMPLATE_SUCCESS'));
        dispatch(newTemplateActions.setAlertType('success'));
    
        // Show loading on workflow group tree until refresh completes
        dispatch(treeActions.setLeftTreeLoading({ screen: parentScreen, data: true }));

        const treeRefreshPromise = dispatch(api.endpoints[treeListEndpoint].initiate({ account }, { forceRefetch: true }));

        try {
          // Wait for tree refresh and template refetch
          await Promise.allSettled([treeRefreshPromise, refetchTemplate()]);
          
          dispatch(treeActions.setLeftTreeContextActionValue({
            screen: parentScreen,
            data: {
              name: selectedNode.title,
              workflowGroup: selectedNode.workflowGroup,
            },
          }));
          
          // Close dialog after successful refetch
          setTimeout(() => {
            toggle();
          }, 1000);
          
        } catch (error) {
          console.error('Error during post-deployment refresh:', error);
          setTimeout(() => {
            toggle();
          }, 1000);
        } finally {
          // Clear tree loading when refresh completes (success or failure)
          dispatch(treeActions.setLeftTreeLoading({ screen: parentScreen, data: false }));
        }

        
        } else {
          setAlertMessage(response.message || t('MSG_DEPLOY_TEMPLATE_FAILED'));
          setAlertType('error');
        }
      } 
      catch (error) {
      console.error('Deploy error:', error);
      setAlertMessage(error.data?.message || t('BOX_MSG_GENERAL_ERROR'));
      setAlertType('error');
    } finally {
      setIsDeploying(false);
    }
  };

  const handleCancel = () => {
    setComment('');
    setAlertMessage('');
    setAlertType('');
    toggle();
  };

  const getItemType = () => {
    switch (parentScreen) {
      case 'inbound':
        return t('INBOUND_TEMPLATE');
      case 'outbound':
        return t('OUTBOUND_TEMPLATE');
      case 'workflow':
        return t('WORKFLOW');
      default:
        return t('TEMPLATE');
    }
  };

  return (
    <>
    <Dialog open={open || false} onClose={handleCancel} maxWidth="sm" fullWidth hidden={showVersionWarning}>
      <DialogTitle className="dialog-header">
        <div className="flex space-between align-center">
          <div className="title">
            {t('DEPLOY_TO_SERVER')}
          </div>
          <IconButton
            onClick={handleCancel}
            sx={{ borderRadius: "5px" }}>
            <CloseIcon
              sx={{
                height: "28px",
                width: "28px",
                color: "#777777",
              }}
            />
          </IconButton>

        </div>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>{t('WARNING')}:</strong> This action will move the {getItemType().toLowerCase()} from your userspace to the server and delete it from userspace. This operation cannot be undone.
        </Typography>

        <TextField
          label={t('LBL_NEW_COMMENT')}
          multiline
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          fullWidth
          margin="normal"
          placeholder={t('LBL_ENTER_DEPLOYMENT_COMMENT')}
        />

        {alertMessage && (
          // <Alert severity={alertType} sx={{ mt: 2 }}>
          //   {alertMessage}
          // </Alert>
          <p className={`${alertType === 'error' ? 'error' : 'success-message'}`}>{alertMessage}</p>
        )}
       
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleCancel} disabled={isDeploying}>
          {t("CANCEL")}
        </Button>
        <Button 
          onClick={handleDeploy} 
          disabled={isDeploying || !selectedNode?.isLeaf || !comment.trim()}
          variant="outlined"
          color="primary"
        >
          {isDeploying ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              {t('DEPLOYING')}...
            </>
          ) : (
            t('DEPLOY_TO_SERVER')
          )}
        </Button>
      </DialogActions>
    </Dialog>
    
    {/* Version Conflict Warning Dialog */}
    <VersionConflictDialog
      open={showVersionWarning}
      onClose={() => {
        setShowVersionWarning(false); 
        setConflictItem(null);
        setComment('');
        setAlertMessage('');
        setAlertType('');
      }}
      conflictItem={conflictItem}
      currentIndex={0}
      totalConflicts={1}
      onResponse={handleVersionConflictResponse}
    />
    </>
  );
}


export default DeployToServer; 